import {
  BookIcon,
  PulseIcon,
  ShieldCheckIcon,
  ShieldLockIcon,
  TriangleDownIcon,
  VerifiedIcon,
  XIcon,
} from '@primer/octicons-react'
import {
  ThemeProvider,
  Hero,
  AnchorNav,
  SectionIntro,
  FAQ,
  FAQGroup,
  Bento,
  Box,
  Text,
  River,
  Heading,
  Stack,
  Card,
  OrderedList,
  UnorderedList,
  Button,
  Image,
  LogoSuite,
  Label,
  Link,
  Testimonial,
} from '@primer/react-brand'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'

export function SecurityIndex() {
  const {requestDemoPath} = useRoutePayload<{requestDemoPath: string}>()
  const {heroContactSalesPath} = useRoutePayload<{heroContactSalesPath: string}>()
  const {navContactSalesPath} = useRoutePayload<{navContactSalesPath: string}>()
  const {pricingContactSalesPath} = useRoutePayload<{pricingContactSalesPath: string}>()

  return (
    <ThemeProvider colorMode="dark" className="lp-Security lp-has-fontSmoothing lp-textWrap--pretty">
      <section id="hero" className="lp-Hero">
        <div className="lp-Container">
          {/* Make space for header + nav */}
          <div className="lp-Spacer" style={{height: '137px'}} aria-hidden />

          <Hero data-hpc>
            <Hero.Label size="large">GitHub Advanced Security</Hero.Label>
            <Hero.Heading size="1" weight="bold" className="lp-Hero-heading">
              Application <br className="lp-breakWhenWide" /> security built in. <br className="lp-breakWhenWide" /> Not
              bolted on.
            </Hero.Heading>
            <Hero.Description>
              <Text size="400" weight="normal" variant="muted">
                With application security tools at their fingertips, developers fix vulnerabilities up to 7x faster than
                those using third-party tools.<sup>1</sup>
              </Text>
            </Hero.Description>
            <Hero.PrimaryAction href={heroContactSalesPath}>Contact sales</Hero.PrimaryAction>
            <Hero.SecondaryAction href={requestDemoPath} hasArrow={false}>
              Request a demo
            </Hero.SecondaryAction>
          </Hero>

          <LogoSuite hasDivider={false}>
            <LogoSuite.Heading visuallyHidden>GitHub Security is used by</LogoSuite.Heading>
            <LogoSuite.Logobar marquee marqueeSpeed="slow">
              <Image src="/images/modules/site/security/2024/logos/telus.svg" alt="Telus's logo" height={36} />
              <Image src="/images/modules/site/security/2024/logos/fidelity.svg" alt="Fidelity's logo" height={36} />
              <Image
                src="/images/modules/site/security/2024/logos/mercado-libre.svg"
                alt="Mercado Libre's logo"
                height={40}
              />
              <Image src="/images/modules/site/security/2024/logos/shopify.svg" alt="Shopify's logo" height={40} />
              <Image src="/images/modules/site/security/2024/logos/linkedin.svg" alt="LinkedIn's logo" height={36} />
              <Image src="/images/modules/site/security/2024/logos/intel.svg" alt="Intel's logo" height={34} />
              <Image src="/images/modules/site/security/2024/logos/3m.svg" alt="3M's logo" height={48} />
              <Image src="/images/modules/site/security/2024/logos/kpmg.svg" alt="KPMG's logo" height={38} />
              <Image
                src="/images/modules/site/security/2024/logos/raiffeisen-bank-international.svg"
                alt="Raiffeisen Bank International's logo"
                height={38}
              />
              <Image
                src="/images/modules/site/security/2024/logos/deutsche-borse.svg"
                alt="Deutsche Börse's logo"
                height={48}
              />
            </LogoSuite.Logobar>
          </LogoSuite>

          <div className="lp-Spacer lp-Spacer--40" aria-hidden />

          <Box className="lp-AnchorNav" style={{height: 0}}>
            <AnchorNav hideUntilSticky>
              <AnchorNav.Link href="#enterprise">Enterprise-ready</AnchorNav.Link>
              <AnchorNav.Link href="#features">Features</AnchorNav.Link>
              <AnchorNav.Link href="#pricing">Pricing</AnchorNav.Link>
              <AnchorNav.Link href="#faq">FAQs</AnchorNav.Link>
              <AnchorNav.Action href={navContactSalesPath}>Contact sales</AnchorNav.Action>
              <AnchorNav.SecondaryAction href={requestDemoPath}>Request a demo</AnchorNav.SecondaryAction>
            </AnchorNav>
          </Box>
        </div>
      </section>

      <section id="enterprise" className="lp-Section--enterprise">
        <div className="lp-Container">
          <div className="lp-Spacer lp-Spacer--56 lp-Spacer--88xl" aria-hidden />

          <SectionIntro align="center" fullWidth>
            <SectionIntro.Label size="large" className="lp-SectionIntroLabel--muted">
              Enterprise-ready
            </SectionIntro.Label>
            <SectionIntro.Heading as="h2" size="2" weight="semibold">
              Fixes in minutes, not months.
            </SectionIntro.Heading>
            <SectionIntro.Description>
              With AI-powered static analysis, secret scanning, and software composition analysis, GitHub Advanced
              Security helps developers and security teams work together to accelerate the delivery of secure software.
            </SectionIntro.Description>
          </SectionIntro>

          <div className="lp-Spacer lp-Spacer--56 lp-Spacer--88xl" aria-hidden />

          <Bento>
            {/* Bento 1 */}
            <Bento.Item columnSpan={{xsmall: 12, medium: 7}} rowSpan={{xsmall: 4, medium: 5}} visualAsBackground>
              <Bento.Content horizontalAlign="center" verticalAlign="center">
                <Bento.Heading as="h3" size="4" weight="semibold">
                  Secure code, accelerated.
                </Bento.Heading>
              </Bento.Content>
              <Bento.Visual>
                <Image
                  src="/images/modules/site/security/2024/enterprise-bento-1.jpg"
                  alt=""
                  width="724"
                  height="500"
                  loading="lazy"
                />
              </Bento.Visual>
            </Bento.Item>

            {/* Bento 2 */}
            {/* <Bento.Item
              columnSpan={{xsmall: 12, medium: 5}}
              rowSpan={{xsmall: 5, medium: 5}}
              className="lp-Enterprise-bentoItem"
              style={{gridTemplateRows: '1fr 1fr', rowGap: '48px'}}
            >
              <Bento.Visual fillMedia={false} horizontalAlign="center" verticalAlign="end">
                <Image
                  src="/images/modules/site/security/2024/enterprise-bento-2.png"
                  alt=""
                  width="120"
                  height="112"
                  loading="lazy"
                />
              </Bento.Visual>
              <Bento.Content horizontalAlign="center" verticalAlign="start">
                <Bento.Heading
                  as="h3"
                  className="lp-is-sansSerifAlt"
                  style={{fontSize: '80px', marginBottom: '24px', lineHeight: '100%'}}
                >
                  48%
                </Bento.Heading>
                <Text as="div" variant="muted">
                  Vulnerabilities fixed in real time, <br className="lp-breakWhenWide" /> more than 7x the industry
                  average.<sup>1</sup>
                </Text>
              </Bento.Content>
            </Bento.Item> */}
            <Bento.Item
              columnSpan={{xsmall: 12, medium: 5}}
              rowSpan={{xsmall: 5, medium: 5}}
              visualAsBackground
              className="lp-Enterprise-bentoItem"
            >
              <Bento.Content
                horizontalAlign="center"
                verticalAlign="center"
                leadingVisual={<PulseIcon />}
                className="lp-Bento--withAccentIcon"
              >
                <Bento.Heading
                  as="h3"
                  className="lp-is-sansSerifAlt"
                  style={{fontSize: '56px', marginBottom: '24px', lineHeight: '100%'}}
                >
                  <span className="lp-Color-accent">7x</span> <br /> faster <br /> fixes
                </Bento.Heading>
                <Text as="div" variant="muted">
                  Detect, prevent, and remediate vulnerabilities more than 7x faster than the industry average.
                  <sup>1</sup>
                </Text>
              </Bento.Content>
            </Bento.Item>

            {/* Bento 3 */}
            <Bento.Item
              columnSpan={{xsmall: 12, medium: 5}}
              rowSpan={{xsmall: 5, medium: 5}}
              visualAsBackground
              className="lp-Enterprise-bentoItem"
            >
              <Bento.Content
                horizontalAlign="center"
                verticalAlign="center"
                leadingVisual={<TriangleDownIcon />}
                className="lp-Bento--withAccentIcon"
              >
                <Bento.Heading
                  as="h3"
                  className="lp-is-sansSerifAlt"
                  style={{fontSize: '56px', marginBottom: '24px', lineHeight: '100%'}}
                >
                  <span className="lp-Color-accent">2.4x</span> <br /> more <br /> precise.
                </Bento.Heading>
                <Text as="div" variant="muted">
                  Finds leaked secrets with <br className="lp-breakWhenWide" /> fewest false positives.<sup>2</sup>
                </Text>
              </Bento.Content>
            </Bento.Item>

            {/* Bento 4 */}
            <Bento.Item
              columnSpan={{xsmall: 12, medium: 7}}
              rowSpan={{xsmall: 4, medium: 5}}
              style={{
                backgroundSize: 'cover',
                backgroundImage: 'url("/images/modules/site/security/2024/enterprise-bento-4.jpg")',
                gridTemplateRows: '1fr 1fr',
                rowGap: '24px',
              }}
            >
              <Bento.Content horizontalAlign="center" verticalAlign="end" leadingVisual={<ShieldLockIcon />}>
                <Bento.Heading as="h3" size="4" weight="semibold">
                  Prevent secret leaks.
                </Bento.Heading>
              </Bento.Content>
              <Bento.Visual fillMedia={false} padding="condensed" horizontalAlign="center" verticalAlign="start">
                <Image
                  src="/images/modules/site/security/2024/enterprise-bento-4-visual.webp"
                  alt=""
                  width="471"
                  height="128"
                  loading="lazy"
                />
              </Bento.Visual>
            </Bento.Item>

            {/* Bento 5 */}
            <Bento.Item
              columnSpan={{xsmall: 12, medium: 7}}
              rowSpan={{xsmall: 4, medium: 5}}
              style={{
                backgroundSize: 'cover',
                backgroundImage: 'url("/images/modules/site/security/2024/enterprise-bento-5.jpg")',
                gridTemplateRows: '1fr 1fr',
                rowGap: '24px',
              }}
            >
              <Bento.Content horizontalAlign="center" verticalAlign="end">
                <Bento.Heading as="h3" size="4" weight="semibold" style={{maxWidth: '20ch'}}>
                  AI-powered code suggestions fix issues for you
                </Bento.Heading>
              </Bento.Content>
              <Bento.Visual fillMedia={false} padding="condensed" horizontalAlign="center" verticalAlign="start">
                <Image
                  src="/images/modules/site/security/2024/enterprise-bento-5-visual.webp"
                  alt=""
                  width="616"
                  height="214"
                  loading="lazy"
                />
              </Bento.Visual>
            </Bento.Item>

            {/* Bento 6 */}
            <Bento.Item
              columnSpan={{xsmall: 12, medium: 5}}
              rowSpan={{xsmall: 4, medium: 5}}
              visualAsBackground
              className="lp-Enterprise-bentoItem"
            >
              <Bento.Content
                horizontalAlign="center"
                verticalAlign="center"
                leadingVisual={<ShieldCheckIcon />}
                className="lp-Bento--withAccentIcon"
              >
                <Bento.Heading as="h3" size="4" weight="semibold">
                  Keep new vulnerabilities out of your code.
                </Bento.Heading>
              </Bento.Content>
            </Bento.Item>

            {/* Bento 7 */}
            <Bento.Item
              columnSpan={12}
              rowSpan={{xsmall: 4, medium: 5}}
              visualAsBackground
              className="lp-Enterprise-bentoItem"
            >
              <Bento.Content horizontalAlign="center" verticalAlign="center">
                <Bento.Heading as="h3" size="4" weight="semibold">
                  <Image
                    src="/images/modules/site/security/2024/enterprise-bento-7-icon.svg"
                    alt="Telus's logo"
                    width="30"
                    height="24"
                    loading="lazy"
                  />
                  <div style={{marginBlockStart: '48px', marginBlockEnd: '48px'}}>
                    Push protection is such a beautiful workflow. We’ve never received a single complaint from
                    developers.
                  </div>
                  <Image
                    src="/images/modules/site/security/2024/enterprise-bento-7-logo.svg"
                    alt="Telus's logo"
                    width="173"
                    height="34"
                    loading="lazy"
                  />
                </Bento.Heading>
              </Bento.Content>
              <Bento.Visual>
                <Image
                  src="/images/modules/site/security/2024/enterprise-bento-7.jpg"
                  alt=""
                  width="1248"
                  height="466"
                  loading="lazy"
                />
              </Bento.Visual>
            </Bento.Item>
          </Bento>

          <div>
            <Image
              src="/images/modules/site/security/2024/enterprise-outro.jpg"
              alt=""
              width={1148}
              height={332}
              loading="lazy"
              style={{display: 'block', marginLeft: 'auto', marginRight: 'auto'}}
            />
          </div>

          {/* <div className="lp-Spacer lp-Spacer--96 lp-Spacer--152xl" aria-hidden /> */}
        </div>
      </section>

      <section id="features" className="lp-Section--features">
        <div className="lp-Container">
          {/* <div className="lp-Spacer lp-Spacer--72 lp-Spacer--128xl" aria-hidden /> */}

          <SectionIntro align="center">
            <SectionIntro.Label size="large" className="lp-SectionIntroLabel--muted">
              Features
            </SectionIntro.Label>
            <SectionIntro.Heading
              as="h2"
              size="3"
              weight="bold"
              className="lp-textWrap--balance"
              style={{maxWidth: '32ch'}}
            >
              For developers who love to code.{' '}
              <span className="lp-Color--muted">
                Detect, prevent and fix vulnerabilities without leaving your flow.
              </span>
            </SectionIntro.Heading>
          </SectionIntro>

          <div className="lp-Spacer lp-Spacer--40" aria-hidden />

          {/* Features River */}

          {/* River 1 */}
          <River imageTextRatio="60:40">
            <River.Content>
              <Heading as="h3" size="5">
                Real-time code scanning
              </Heading>
              <Text as="p" className="lp-River-text">
                With actionable feedback in the pull request, code scanning helps you find, triage, and prioritize
                security vulnerabilities right in your workflow.
              </Text>
              <Link href="#TODO">Explore code scanning</Link>
            </River.Content>
            <River.Visual>
              <Image
                src="/images/modules/site/security/2024/features-river-1.webp"
                alt="TODO"
                width="708"
                height="510"
                style={{display: 'block'}}
                loading="lazy"
              />
            </River.Visual>
          </River>

          {/* River 2 */}
          <River imageTextRatio="60:40">
            <River.Content>
              <Heading as="h3" size="5">
                <Label className="lp-Label--inRiver">Available in public beta – March, 2024</Label>
                AI-powered security fixes.
              </Heading>
              <Text as="p" className="lp-River-text">
                Secret scanning with push protection guards over 200 token types and patterns from more than 180 service
                providers, including secrets unique to your organization.
              </Text>
              <Link href="#TODO">Explore autofix</Link>
            </River.Content>
            <River.Visual>
              <Image
                src="/images/modules/site/security/2024/features-river-2.webp"
                alt="TODO"
                width="708"
                height="510"
                style={{display: 'block'}}
                loading="lazy"
              />
            </River.Visual>
          </River>

          {/* River 3 */}
          <River imageTextRatio="60:40">
            <River.Content>
              <Heading as="h3" size="5">
                Secure your secrets, protect your business.
              </Heading>
              <Text as="p" className="lp-River-text">
                Secret scanning with push protection guards over 200 token types and patterns from more than 180 service
                providers, including secrets unique to your organization.
              </Text>
              <Link href="#TODO">Explore secret scanning</Link>
            </River.Content>
            <River.Visual>
              <Image
                src="/images/modules/site/security/2024/features-river-3.webp"
                alt="TODO"
                width="708"
                height="510"
                style={{display: 'block'}}
                loading="lazy"
              />
            </River.Visual>
          </River>

          <div className="lp-Spacer lp-Spacer--48 lp-Spacer--104xl" aria-hidden />

          <Testimonial size="large" className="lp-Features-testimonial">
            <Testimonial.Quote className="lp-Features-testimonialQuote">
              GitHub Advanced Security helps developers fix potential issues before production.
            </Testimonial.Quote>
            <Testimonial.Logo className="lp-Features-testimonialLogo">
              <img
                src="/images/modules/site/security/2024/features-testimonial-logo.svg"
                alt="Mercado Libre's logo"
                width="157"
                height="40"
              />
            </Testimonial.Logo>
          </Testimonial>

          {/* Features Bento */}

          {/* Bento 1 */}
          <Bento>
            <Bento.Item columnSpan={12} rowSpan={{xsmall: 5, medium: 6}} className="lp-Features-bentoItem">
              <Bento.Content horizontalAlign="center" verticalAlign="center">
                <Bento.Heading as="h3" size="4" weight="semibold">
                  Dependencies you can depend on.
                </Bento.Heading>
                <Text as="div" variant="muted" style={{maxWidth: '460px'}}>
                  Secure, manage, and report on software supply chains with automated security and version updates and
                  one-click software bills of material (SBOMs).
                </Text>
                <Link href="#TODO">Secure your end-to-end supply chain</Link>
              </Bento.Content>
              <Bento.Visual fillMedia={false} padding="normal" horizontalAlign="center" verticalAlign="start">
                <Image
                  src="/images/modules/site/security/2024/features-bento-1.webp"
                  alt=""
                  width="915"
                  height="544"
                  loading="lazy"
                  className="lp-borderRadiusNone"
                />
              </Bento.Visual>
            </Bento.Item>

            {/* Bento 2 */}
            <Bento.Item
              columnSpan={{xsmall: 12, medium: 6}}
              rowSpan={{xsmall: 4, medium: 6}}
              className="lp-Features-bentoItem"
              style={{
                rowGap: '44px',
              }}
            >
              <Bento.Content horizontalAlign="start" verticalAlign="end">
                <Bento.Heading as="h3" size="5" weight="semibold" style={{maxWidth: '20ch'}}>
                  Security status at a glance.
                </Bento.Heading>
                <Text as="div" variant="muted">
                  Verify progress and track trends to prioritize remediation tasks across multiple repositories.
                </Text>
                <Link href="#TODO">Discover security overview</Link>
              </Bento.Content>
              <Bento.Visual
                fillMedia={false}
                position="0% 0%"
                padding="spacious"
                style={{paddingRight: '0', paddingBottom: '0'}}
              >
                <Image
                  src="/images/modules/site/security/2024/features-bento-2.webp"
                  alt=""
                  width="550"
                  height="409"
                  loading="lazy"
                  className="lp-borderRadiusNone"
                />
              </Bento.Visual>
            </Bento.Item>

            {/* Bento 3 */}
            <Bento.Item
              columnSpan={{xsmall: 12, medium: 6}}
              rowSpan={{xsmall: 4, medium: 6}}
              className="lp-Features-bentoItem"
            >
              <Bento.Content horizontalAlign="start" verticalAlign="end">
                <Bento.Heading as="h3" size="5" weight="semibold">
                  Your workflows, your tools.
                </Bento.Heading>
                <Text as="div" variant="muted">
                  With support for more than 17,000 app integrations and actions templates, GitHub Advanced Security
                  provides one workflow for your entire toolchain.
                </Text>
                <Link href="#TODO">Explore GitHub Marketplace</Link>
              </Bento.Content>
              <Bento.Visual position="0% 0%" style={{paddingBlockEnd: '80px'}}>
                <Image
                  src="/images/modules/site/security/2024/features-bento-3.webp"
                  alt=""
                  width="606"
                  height="208"
                  loading="lazy"
                />
              </Bento.Visual>
            </Bento.Item>

            {/* Bento 4 */}
            <Bento.Item
              columnSpan={12}
              rowSpan={{xsmall: 8, medium: 5}}
              flow={{xsmall: 'row', medium: 'column'}}
              className="lp-Features-bentoItem"
            >
              <Bento.Content
                horizontalAlign="start"
                verticalAlign="center"
                leadingVisual={<VerifiedIcon />}
                className="lp-Bento--withAccentIcon"
              >
                <Bento.Heading as="h3" size="5" weight="semibold">
                  Security expertise at your command.
                </Bento.Heading>
                <Text as="div" variant="muted">
                  Powered by security experts and a global community of more than 100m developers, GitHub Advanced
                  Security provides the insights and automation you need to ship secure software on schedule.
                </Text>
                <Link href="#TODO">Visit the Security Lab</Link>
              </Bento.Content>
              <Bento.Visual
                fillMedia={false}
                padding="spacious"
                horizontalAlign="start"
                verticalAlign="center"
                style={{paddingInlineStart: '0'}}
              >
                <Image
                  src="/images/modules/site/security/2024/features-bento-4.webp"
                  alt=""
                  width="566"
                  height="402"
                  loading="lazy"
                  className="lp-borderRadiusNone"
                />
              </Bento.Visual>
            </Bento.Item>
          </Bento>

          <div className="lp-Spacer lp-Spacer--48 lp-Spacer--120xl" aria-hidden />
        </div>
      </section>

      <section id="pricing" className="lp-Section--pricing">
        <div className="lp-Container">
          <div className="lp-Spacer lp-Spacer--vw18 lp-Spacer--306xl" aria-hidden />

          <SectionIntro align="center" fullWidth>
            <SectionIntro.Label size="large" className="lp-SectionIntroLabel--muted">
              Pricing
            </SectionIntro.Label>
            <SectionIntro.Heading size="3">
              Enable native security <br /> for every repository.
            </SectionIntro.Heading>
            <SectionIntro.Description>
              Eliminate toolchain cost and complexity with native security tools for GitHub Enterprise and Azure DevOps.
            </SectionIntro.Description>
          </SectionIntro>

          <div className="lp-Spacer lp-Spacer--40" aria-hidden />

          <Stack
            className="lp-Pricing-card"
            direction={{narrow: 'vertical', regular: 'horizontal'}}
            gap="none"
            padding="none"
          >
            {/* Standard */}

            <Stack padding="none" gap={24} style={{flex: '1'}}>
              <Box>
                <Text size="100" weight="semibold" className="lp-Pricing-label">
                  Available for all
                </Text>
                <Box marginBlockStart={8} marginBlockEnd={16}>
                  <Heading as="h3" size="5">
                    Standard Security
                  </Heading>
                </Box>
                <Text as="p" size="200" weight="normal" variant="muted" className="lp-Pricing-description">
                  Manage and secure open source software and dependencies.
                </Text>
              </Box>

              <Stack direction="horizontal" gap={12} padding="none" className="lp-Pricing-price">
                <Text size="500" weight="normal" className="lp-is-sansSerifAlt" style={{lineHeight: 1.3}}>
                  $
                </Text>
                <Text weight="normal" className="lp-Pricing-nr lp-is-sansSerifAlt">
                  0
                </Text>
                <Text size="200" weight="normal" style={{alignSelf: 'end', lineHeight: 1.4}}>
                  For all users <br /> and plans
                </Text>
              </Stack>

              <Button
                as="a"
                href="https://docs.github.com/code-security/getting-started/github-security-features"
                block
              >
                Learn more
              </Button>

              <hr className="lp-Pricing-divider" />

              <div>
                <Text size="100" weight="semibold" className="lp-Pricing-listTitle lp-Pricing-label">
                  What’s included:
                </Text>
                <UnorderedList variant="checked" className="lp-Pricing-list">
                  <UnorderedList.Item>Dependabot alerts</UnorderedList.Item>
                  <UnorderedList.Item>Dependabot version and security updates</UnorderedList.Item>
                  <UnorderedList.Item>Security overview</UnorderedList.Item>
                  <UnorderedList.Item>Advisory database</UnorderedList.Item>
                  <UnorderedList.Item>Vulnerability reporting</UnorderedList.Item>
                </UnorderedList>
                <UnorderedList className="lp-Pricing-list lp-Pricing-list--excluded" aria-label="What’s not included:">
                  <UnorderedList.Item>
                    <XIcon />
                    Code scanning with CodeQL
                  </UnorderedList.Item>
                  <UnorderedList.Item>
                    <XIcon />
                    AI-powered autofix*
                  </UnorderedList.Item>
                  <UnorderedList.Item>
                    <XIcon />
                    Secret scanning alerts for all repositories
                  </UnorderedList.Item>
                  <UnorderedList.Item>
                    <XIcon />
                    Secret scanning push protection
                  </UnorderedList.Item>
                  <UnorderedList.Item>
                    <XIcon />
                    Dependency review
                  </UnorderedList.Item>
                  <UnorderedList.Item>
                    <XIcon />
                    Auto-triage rules for Dependabot
                  </UnorderedList.Item>
                  <UnorderedList.Item>
                    <XIcon />
                    Push-button enablement
                  </UnorderedList.Item>
                  <UnorderedList.Item>
                    <XIcon />
                    Security overview dashboards
                  </UnorderedList.Item>
                </UnorderedList>
              </div>
            </Stack>

            <hr className="lp-Pricing-divider lp-Pricing-divider--vertical" />

            {/* Advanced */}

            <Stack padding="none" gap={24} style={{flex: '1'}}>
              <Box>
                <Text size="100" weight="semibold" className="lp-Pricing-label">
                  Requires GitHub Enterprise
                </Text>
                <Box marginBlockStart={8} marginBlockEnd={16}>
                  <Heading as="h3" size="5">
                    GitHub Advanced Security
                  </Heading>
                </Box>
                <Text as="p" size="200" weight="normal" variant="muted" className="lp-Pricing-description">
                  Detect, prevent, and remediate vulnerabilities in all of your repositories (requires GitHub Enterprise
                  or Azure DevOps).
                </Text>
              </Box>

              <Stack direction="horizontal" gap={12} padding="none" className="lp-Pricing-price">
                <Text size="500" weight="normal" className="lp-is-sansSerifAlt" style={{lineHeight: 1.3}}>
                  $
                </Text>
                <Text weight="normal" className="lp-Pricing-nr lp-is-sansSerifAlt">
                  49
                </Text>
                <Text size="200" weight="normal" style={{alignSelf: 'end', lineHeight: 1.4}}>
                  per month / <br /> per active committer
                </Text>
              </Stack>

              <Button as="a" href={pricingContactSalesPath} variant="primary">
                Contact sales
              </Button>

              <hr className="lp-Pricing-divider" />

              <div>
                <Text size="100" weight="semibold" className="lp-Pricing-listTitle lp-Pricing-label">
                  What’s included:
                </Text>
                <UnorderedList variant="checked" className="lp-Pricing-list">
                  <UnorderedList.Item>Dependabot alerts</UnorderedList.Item>
                  <UnorderedList.Item>Dependabot version and security updates</UnorderedList.Item>
                  <UnorderedList.Item>Security overview</UnorderedList.Item>
                  <UnorderedList.Item>Advisory database</UnorderedList.Item>
                  <UnorderedList.Item>Vulnerability reporting</UnorderedList.Item>
                  <UnorderedList.Item>Code scanning with CodeQL</UnorderedList.Item>
                  <UnorderedList.Item>AI-powered autofix*</UnorderedList.Item>
                  <UnorderedList.Item>Secret scanning alerts for all repositories</UnorderedList.Item>
                  <UnorderedList.Item>Secret scanning push protection</UnorderedList.Item>
                  <UnorderedList.Item>Dependency review</UnorderedList.Item>
                  <UnorderedList.Item>Auto-triage rules for Dependabot</UnorderedList.Item>
                  <UnorderedList.Item>Push-button enablement</UnorderedList.Item>
                  <UnorderedList.Item>Security overview dashboards</UnorderedList.Item>
                </UnorderedList>
              </div>
            </Stack>
          </Stack>

          <div className="lp-Spacer lp-Spacer--72 lp-Spacer--128xl" aria-hidden />
        </div>
      </section>

      <section id="resources">
        <div className="lp-Container">
          <div className="lp-Spacer lp-Spacer--48 lp-Spacer--104xl" aria-hidden />

          <SectionIntro align="center" fullWidth>
            <SectionIntro.Heading size="3">
              Get the most out of <br className="lp-break-whenWide" /> GitHub Advanced Security.
            </SectionIntro.Heading>
          </SectionIntro>

          <div className="lp-Spacer lp-Spacer--40" aria-hidden />

          <Stack
            direction={{narrow: 'vertical', regular: 'horizontal', wide: 'horizontal'}}
            gap="normal"
            padding="none"
          >
            <div className="lp-Resources-card">
              <Card
                ctaText="Get started with GHAS"
                href="https://docs.github.com/code-security/getting-started/github-security-features"
              >
                <Card.Icon icon={<BookIcon />} color="blue" hasBackground />
                <Card.Heading>How to get started with GitHub Advanced Security (GHAS)</Card.Heading>
                <Card.Description>
                  Learn about GitHub Advanced Security and how it can benefit your organization.
                </Card.Description>
              </Card>
            </div>
            <div className="lp-Resources-card">
              <Card
                ctaText="Read the Forrester Report"
                href="https://resources.github.com/forrester-industry-spotlight-github-advanced-security/"
              >
                <Card.Icon icon={<BookIcon />} color="blue" hasBackground />
                <Card.Heading>GitHub TEI spotlight for GitHub Advanced Security</Card.Heading>
                <Card.Description>
                  Read about the benefits of improving software security standards in organizations
                </Card.Description>
              </Card>
            </div>
            <div className="lp-Resources-card">
              <Card
                ctaText="Watch the videos"
                href="https://resources.github.com/security/what-is-github-advanced-security/"
              >
                <Card.Icon icon={<BookIcon />} color="blue" hasBackground />
                <Card.Heading>Security playlist</Card.Heading>
                <Card.Description>
                  Learn how industry experts use GitHub Advanced Security to protect their code without sacrificing
                  developer productivity.
                </Card.Description>
              </Card>
            </div>
          </Stack>
        </div>
      </section>

      <section id="faq">
        <div className="lp-Container">
          <div className="lp-Spacer lp-Spacer--72 lp-Spacer--128xl" aria-hidden />

          <FAQGroup>
            <FAQGroup.Heading align="center">Frequently asked questions.</FAQGroup.Heading>

            <FAQ>
              <FAQ.Heading>Group 1</FAQ.Heading>
              <FAQ.Item>
                <FAQ.Question>Question 1</FAQ.Question>
                <FAQ.Answer>
                  <p>Answer 1</p>
                </FAQ.Answer>
              </FAQ.Item>
              <FAQ.Item>
                <FAQ.Question>Question 2</FAQ.Question>
                <FAQ.Answer>
                  <p>Answer 2</p>
                </FAQ.Answer>
              </FAQ.Item>
              <FAQ.Item>
                <FAQ.Question>Question 3</FAQ.Question>
                <FAQ.Answer>
                  <p>Answer 3</p>
                </FAQ.Answer>
              </FAQ.Item>
            </FAQ>

            <FAQ>
              <FAQ.Heading>Group 2</FAQ.Heading>
              <FAQ.Item>
                <FAQ.Question>Question 1</FAQ.Question>
                <FAQ.Answer>
                  <p>Answer 1</p>
                </FAQ.Answer>
              </FAQ.Item>
              <FAQ.Item>
                <FAQ.Question>Question 2</FAQ.Question>
                <FAQ.Answer>
                  <p>Answer 2</p>
                </FAQ.Answer>
              </FAQ.Item>
              <FAQ.Item>
                <FAQ.Question>Question 3</FAQ.Question>
                <FAQ.Answer>
                  <p>Answer 3</p>
                </FAQ.Answer>
              </FAQ.Item>
            </FAQ>
          </FAQGroup>

          <div className="lp-Spacer lp-Spacer--72 lp-Spacer--128xl" aria-hidden />
        </div>
      </section>

      <section id="footnotes">
        <div className="lp-Container">
          <div className="lp-Spacer lp-Spacer--40" aria-hidden />

          <OrderedList>
            <OrderedList.Item className="lp-Footnotes-item">
              Based on data from the industry’s longest running analysis of fix rates, Veracode State of Software
              Security 2023. Developers with GitHub Advanced Security fix 72% of vulnerabilities within 28 days, more
              than 7x faster than the industry average, where it takes 198 days to reach a 50% fix rate.
            </OrderedList.Item>
          </OrderedList>

          <div className="lp-Spacer lp-Spacer--64" aria-hidden />
        </div>
      </section>
    </ThemeProvider>
  )
}

try{ SecurityIndex.displayName ||= 'SecurityIndex' } catch {}